import { addWildcardToRoutes, RouteElement, useNavigate, withOutlet } from '@melio/ar-domain';
import { forwardRef } from '@melio/platform-utils';
import { Route, Routes } from 'react-router-dom';

import { CreateInvoiceActivity as _CreateInvoiceActivity } from '../activities';
import { IssueInvoiceFlow } from './IssueInvoice.flow';
import { useInvoiceModals } from './useInvoiceModals';

const CreateInvoiceActivity = withOutlet(_CreateInvoiceActivity);

export type CreateInvoiceFlowProps = {
  onClose: VoidFunction;
  onDone: (id?: string) => void;
  onError?: ErrorFunction;
};

export const CreateInvoiceFlow = forwardRef<CreateInvoiceFlowProps>(({ onClose, onDone, ...props }, ref) => {
  const { Paths, goToCreateInvoice, goToIssueInvoice } = useCreateInvoiceFlowNavigation();

  const {
    renderModals,
    openTaxRateModal,
    openDiscountModal,
    openServiceChargeModal,
    removeServiceCharge,
    discount,
    serviceCharges,
    removeDiscount,
    taxRate,
  } = useInvoiceModals({ onClose: goToCreateInvoice });

  const onCreateDone = (id: string, shouldIssue?: boolean) => {
    if (!shouldIssue) {
      onDone(id);
    } else {
      goToIssueInvoice(id);
    }
  };

  return (
    <Routes>
      <Route
        path={Paths.Home}
        element={
          <CreateInvoiceActivity
            discountId={discount?.id}
            onSetDiscount={openDiscountModal}
            onRemoveDiscount={removeDiscount}
            onClose={onClose}
            onDone={onCreateDone}
            taxRateId={taxRate?.id}
            onSetTaxRate={openTaxRateModal}
            serviceChargeIds={serviceCharges?.map((sc) => sc.serviceChargeCatalogId)}
            onAddServiceCharge={openServiceChargeModal}
            onRemoveServiceCharge={removeServiceCharge}
            {...props}
            ref={ref}
          />
        }
      >
        {renderModals()}
      </Route>
      <Route
        path={Paths.IssueInvoice}
        element={
          <RouteElement
            component={IssueInvoiceFlow}
            pathToProps={{ id: 'invoiceId' }}
            onClose={onClose}
            onDone={onDone}
          />
        }
      />
    </Routes>
  );
});
CreateInvoiceFlow.displayName = 'CreateInvoiceFlow';

const useCreateInvoiceFlowNavigation = () => {
  enum Paths {
    Home = '',
    IssueInvoice = 'issue/:id',
  }

  const navigate = useNavigate<Paths>({ withSearchparams: true });

  const goToCreateInvoice = (options?: Parameters<typeof navigate>[1]) => navigate(Paths.Home, options);
  const goToIssueInvoice = (id: string) => navigate(Paths.IssueInvoice, { replace: true, pathParams: { id } });

  return {
    Paths: addWildcardToRoutes(Paths),
    goToCreateInvoice,
    goToIssueInvoice,
  };
};
